import { useState, useEffect, useCallback, useMemo } from 'react';
import { getJobs } from '../api/jobs-api';
import type { JobListItemDto, JobsQueryParams } from '../types';

/**
 * Hook for fetching and managing jobs list
 */
export function useJobs(params?: JobsQueryParams) {
  const [data, setData] = useState<JobListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize the params to avoid unnecessary re-renders
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const jobs = await getJobs(params);
      setData(jobs);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch jobs');
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchJobs,
  };
}
